import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ImagePopUp from "./grnpopup/ImagePopUp";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { AnyForNow } from "../../features/grn/types";
import { useSelector } from "react-redux";
import { externalgetApi } from "../../services/externalApis";
import { SETTING_API_03, SETTING_API_09 } from "../../api/ApiModel";
import { ENDPOINT } from "../../api/EndPoints";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants";

interface ViewGrnAttachmentsProps {
  attachments?: AnyForNow;
  grnDocuments?: AnyForNow;
  grnData?: AnyForNow;
}

const ViewGrnAttachments: React.FC<ViewGrnAttachmentsProps> = (props) => {
  const { attachments, grnDocuments } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const [imagePopupOpen, setImagePopupOpen] = useState<boolean>(false);
  const [selectedImageData, setSelectedImageData] = useState<AnyForNow>(null);
  const [grnDocTypes, setGrnDocTypes] = useState<AnyForNow[]>([]);
  
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};

  const isImage = (filename) => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename);

  // Helper function to check if a string is valid base64
  const isBase64String = (str: string): boolean => {
    if (!str || typeof str !== 'string') return false;
    // Base64 strings typically don't contain JSON characters like {, }, [, ]
    // and are longer alphanumeric strings with +, /, = characters
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    return base64Regex.test(str.trim()) && str.length > 50 && !str.trim().startsWith('[') && !str.trim().startsWith('{');
  };

  // Fetch GRN document types
  const fetchGrnDocType = useCallback(async (docCatCode: string | number): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      console.warn("Cannot fetch GRN document types: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
      return;
    }

    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        DOCCATCODE: docCatCode,
        STS: CON_STATUS_ACTIVE,
      };
      console.log("Fetching GRN document types with params:", params);
      const response = await externalgetApi(SETTING_API_03, "GET", params);
      const docTypes = Array.isArray(response) ? response : [];
      console.log("Fetched GRN document types:", docTypes);
      setGrnDocTypes(docTypes);
    } catch (error) {
      console.error("Error fetching GRN document types", error);
      setGrnDocTypes([]);
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Fetch document category first, then fetch doctypes
  const fetchGrnDocCategory = useCallback(async (): Promise<void> => {
    try {
      const params = { STS: CON_STATUS_ACTIVE };
      const response = await externalgetApi(SETTING_API_09, "GET", params);
      const grnCategory = Array.isArray(response) 
        ? response.find((cat: AnyForNow) => cat.doccatname === "Item-Based Document Types")
        : null;

      if (grnCategory) {
        await fetchGrnDocType(grnCategory.doccatcode);
      } else {
        // Fallback: try with DOCCATCODE=1 directly (as per user's API example)
        await fetchGrnDocType(1);
      }
      console.log("grnCategory", grnCategory);
    } catch (error) {
      console.error("Error fetching GRN document category", error);
      // Fallback: try with DOCCATCODE=1 directly
      await fetchGrnDocType(1);
    }
  }, [fetchGrnDocType]);

  // Fetch doctypes on component mount
  useEffect(() => {
    fetchGrnDocCategory();
  }, [fetchGrnDocCategory]);

  // Function to get doctype name from doctypecode
  const getDocTypeName = (code: string | number | undefined): string => {
    if (!code) return "-";
    const found = grnDocTypes.find(
      (doc: AnyForNow) => String(doc.doctypecode) === String(code)
    );
    return found ? found.doctype : String(code) || "-";
  };

  // Parse GRN attachments - handles both JSON format (saved) and base64 format (new)
  // Use docpathjson or bytarr as file data source (both may contain base64)
  const parsedAttachmentFiles = (Array.isArray(attachments) ? attachments : attachments ? [attachments] : []).flatMap((attachment) => {
    const fileData = attachment?.docpathjson || attachment?.bytarr || "";
    if (!fileData) {
      return [];
    }

    try {
      // Try to parse as JSON (for saved attachments from server)
      const parsed = JSON.parse(fileData);

      // Handle array format: [{FILEPATH, FILENAME, ...}, ...]
      if (Array.isArray(parsed)) {
        return parsed.map((file) => ({
          fileUrl: file.FILEPATH,
          fileName: file.FILENAME || file.FILEPATH?.split("/").pop(),
          fileType: file.FILEPATH?.split(".").pop(),
          REMARK: file.REMARK || "",
          DOCTYPECODE: file.DOCTYPECODE || "",
          doctypeothername: file.DOCTYPEOTHERNAME || "",
          ...attachment,
        }));
      }

      // Handle single-object format: {FILEPATH, FILENAME, ...}
      if (parsed && typeof parsed === "object") {
        return [{
          fileUrl: parsed.FILEPATH,
          fileName: parsed.FILENAME || parsed.FILEPATH?.split("/").pop(),
          fileType: parsed.FILEPATH?.split(".").pop(),
          REMARK: parsed.REMARK || "",
          DOCTYPECODE: parsed.DOCTYPECODE || "",
          doctypeothername: parsed.DOCTYPEOTHERNAME || "",
          ...attachment,
        }];
      }
    } catch (parseError) {
      // If JSON parse fails, check if it's a base64 string (new attachment format)
      if (isBase64String(fileData)) {
        const fileName = attachment.fileName || attachment.filename || "attachment";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");

        return [{
          fileUrl: isPdf
            ? `data:application/pdf;base64,${fileData}`
            : `data:image/*;base64,${fileData}`,
          fileName: fileName,
          fileType: fileName.split(".").pop(),
          REMARK: attachment.remark || attachment.docremark || "",
          doctype: attachment.doctype || "",
          doctypeothername: attachment.doctypeothername || "",
          docremark: attachment.docremark || attachment.remark || "",
          docpathjson: fileData,
          ...attachment,
        }];
      }
      // Path or URL from upload (path-only attachment from Create GRN → View GRN)
      if (fileData && typeof fileData === "string" && fileData.trim() !== "") {
        const isFullUrl = fileData.startsWith("http://") || fileData.startsWith("https://");
        const fileUrl = isFullUrl
          ? fileData
          : ENDPOINT.replace(/\/api\/?$/, "") + (fileData.startsWith("/") ? fileData : "/" + fileData);
        const fileName =
          attachment.fileName || attachment.filename || attachment.FILENAME || fileData.split("/").pop() || "attachment";
        return [{
          fileUrl,
          fileName,
          fileType: fileName.split(".").pop(),
          REMARK: attachment.remark || attachment.docremark || "",
          DOCTYPECODE: attachment.doctypecode,
          doctype: attachment.doctype || "",
          doctypeothername: attachment.doctypeothername || "",
          docremark: attachment.docremark || attachment.remark || "",
          docpathjson: fileData,
          ...attachment,
        }];
      }
      console.warn("Attachment fileData is neither valid JSON nor base64 nor path:", attachment);
    }

    return [];
  });

  const processedGRNDocs = (Array.isArray(grnDocuments) ? grnDocuments : grnDocuments ? [grnDocuments] : []).flatMap((doc) => {
    const rawJson = doc?.docpathjson || "";
    if (!rawJson) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawJson || "[]");
      // Normalize to array: support both array and single-object formats
      const files = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === "object"
        ? [parsed]
        : [];

      return files.map((file) => {
        const base64 = file.docpathjson || file.bytarr || "";
        const fileName = file.FILENAME || file.FILEPATH?.split("/").pop() || "attachment";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");
        const fileUrl = file.FILEPATH || (base64 && isBase64String(base64)
          ? (isPdf ? `data:application/pdf;base64,${base64}` : `data:image/*;base64,${base64}`)
          : "");

        return {
          fileUrl,
          fileName,
          fileType: file.FILEPATH?.split(".").pop() || fileName.split(".").pop(),
          REMARK: file.REMARK || "",
          DOCTYPECODE: file.DOCTYPECODE || "",
          doctypeothername: file.DOCTYPEOTHERNAME || "",
          docpathjson: base64 || file.FILEPATH,
          ...doc,
        };
      });
    } catch (error) {
      console.error("Invalid JSON in docpathjson", doc?.docpathjson);
      return [];
    }
  });

  // Merge both sources so all attachments from Create (in-memory) and API (grnDocuments) are shown.
  // Deduplicate by fileName + DOCTYPECODE to avoid showing the same file twice when both sources have it.
  const seenKeys = new Set<string>();
  const displayFiles = [...parsedAttachmentFiles, ...processedGRNDocs].filter((file) => {
    const key = `${file.fileName || ""}-${file.DOCTYPECODE ?? file.doctypecode ?? ""}-${file.fileUrl?.slice?.(0, 50) || ""}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  return (
    <>
<Box display="flex" justifyContent="flex-start" gap={2} mt={2}   flexDirection="column">
        {displayFiles?.map((attachment, index) => (
          <Box
            key={index}
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            mt={0}
          >
            {isImage(attachment.fileName) ? (
              <img
                src={
                  attachment.fileUrl ||
                  `data:image/png;base64,${attachment.docpathjson}`
                }
                alt={attachment.fileName}
                style={{ 
                  width: 40, 
                  height: 40, 
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "2px",
                  display: "block",
                }}
                onClick={() => {
                  // If fileUrl exists (already a data URL), use it directly
                  // Otherwise, construct from docpathjson base64
                  const fileUrlToUse = attachment.fileUrl || 
                    (attachment.docpathjson 
                      ? `data:${
                          isImage(attachment.fileName)
                            ? "image/*"
                            : "application/pdf"
                        };base64,${attachment.docpathjson}`
                      : null);

                  setSelectedImageData({
                    fileContent: attachment.docpathjson || attachment.fileUrl?.split(",")?.[1] || "",
                    fileType: isImage(attachment.fileName) ? "image" : "pdf",
                    fileName: attachment.fileName,
                    remark: attachment.docremark || attachment.REMARK || attachment.remark || "",
                    fullBase64: fileUrlToUse,
                  });

                  setImagePopupOpen(true);
                }}
              />
            ) : (
              <InsertDriveFileIcon
                style={{ 
                  width: 40, 
                  height: 40,
                  cursor: "pointer",
                  borderRadius: "2px",
                  padding: "2px",
                }}
                onClick={() => {
                  // If fileUrl exists (already a data URL), use it directly
                  // Otherwise, construct from docpathjson base64
                  const fileUrlToUse = attachment.fileUrl || 
                    (attachment.docpathjson 
                      ? `data:${
                          isImage(attachment.fileName)
                            ? "image/*"
                            : "application/pdf"
                        };base64,${attachment.docpathjson}`
                      : null);

                  setSelectedImageData({
                    fileContent: attachment.docpathjson || attachment.fileUrl?.split(",")?.[1] || "",
                    fileType: isImage(attachment.fileName) ? "image" : "pdf",
                    fileName: attachment.fileName,
                    remark: attachment.docremark || attachment.REMARK || attachment.remark || "",
                    fullBase64: fileUrlToUse,
                  });

                  setImagePopupOpen(true);
                }}
              />
            )}

            {/* Document Info Card */}
   
<Paper
  sx={{
    p: 2,
    borderRadius: 2,
    boxShadow: 1,
    maxWidth: 600,
    mx: "auto",
  }}
>
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
    {/* Document Type */}
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 200 }}>
      {/* <Typography
        variant="subtitle2"
        sx={{ mr: 1, color: "text.secondary", whiteSpace: "nowrap" }}
      >
        Document Type:
      </Typography> */}
     <Typography
   variant="body1"
  
  >
    {getDocTypeName(attachment.DOCTYPECODE || attachment.doctypecode || attachment.doctype)}
  </Typography>
  {(attachment.doctypeothername || attachment.DOCTYPEOTHERNAME) && (
    <Typography
   variant="body1"
      sx={{
    
        ml: 0.5,
      }}
    >
      -{attachment.doctypeothername || attachment.DOCTYPEOTHERNAME}
    </Typography>
  )}
      <Tooltip title={attachment.docremark || ""} arrow>
        <Typography
          variant="body1"
          sx={{
            maxWidth: 250,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            cursor: "pointer",
          }}
        >
          -{attachment.docremark || attachment.REMARK || attachment.remark }
        </Typography>
      </Tooltip>
    </Box>
  </Box>
</Paper>
          </Box>
        ))}
      </Box>

      {/* Popup */}
      <ImagePopUp
        open={imagePopupOpen}
        handleClose={() => setImagePopupOpen(false)}
        image={selectedImageData?.fullBase64}
        name={selectedImageData?.fileName}
        description={selectedImageData?.remark}
      />
    </>
  );
};

export default ViewGrnAttachments;
